import * as React from "react";

import * as Cmp from "@impero-com/common/utils/comparison";
import * as DV from "@impero-com/common/utils/dataView";
import { getParentElement } from "@impero-com/common/utils/dom";
import { t } from "@impero-com/common/utils/i18n";
import {
    addFieldFilter,
    DataViewIndex,
    QueryResult,
    removeFieldFilter,
    SimpleAndFilter,
    SimpleFieldFilter,
} from "@impero-com/common/utils/indexing";
import { assertNever } from "@impero-com/common/utils/obj";
import { Button, InformationModal, renderMarkdown } from "@impero-com/ui";
import { AutoComplete } from "antd";
import { SelectValue } from "antd/es/select";
import { isEmpty } from "lodash-es";
import memoizeOne from "memoize-one";

import { EntityView } from "api/generated/model";
import { getRisksRiskDirectoryIdNodes } from "api/generated/risks/risks";
import { EditInput, EditProps } from "components/form/legacyFields";
import * as R from "org/risks/types";
import {
    casterWithIdWrapper,
    castValue,
    EditFormCreate,
    FormComponentProps,
    ValidationRule,
} from "utils/form";

import * as T from "../content/types";
import { getDirectoryChildren, getId } from "../content/utils";

import { AssignEntitiesTable, TableEntity } from "./AssignEntitiesTable";
import * as theme from "./theme.css";

class AssignEntitiesComponent extends React.PureComponent<
    AssignEntitiesComponentProps,
    AssignEntitiesComponentState
> {
    entitiesDataView = memoizeOne(
        (
            ancestorsEntities: Record<number, string>,
            nodeEntities: number[],
            entities: Record<number, EntityView>
        ) =>
            this.buildEntitiesDataView(
                ancestorsEntities,
                nodeEntities,
                entities
            )
    );
    entitiesDataViewIndex = memoizeOne(
        (
            ancestorsEntities: Record<number, string>,
            nodeEntities: number[],
            entities: Record<number, EntityView>
        ) =>
            this.buildEntitiesDataViewIndex(
                this.entitiesDataView(ancestorsEntities, nodeEntities, entities)
            )
    );
    entitiesSuggestions = memoizeOne(
        (
            entities: Record<number, EntityView>,
            ancestorsEntities: Record<number, string>,
            nodeEntities: number[],
            descendantsEntities?: Record<number, string[]>
        ) =>
            this.buildEntitiesSuggestions(
                entities,
                ancestorsEntities,
                nodeEntities,
                descendantsEntities
            )
    );

    constructor(props: AssignEntitiesComponentProps) {
        super(props);
        const {
            ancestorsEntities,
            nodeEntities,
            descendantsEntities,
            entities,
        } = props;
        const entitiesDataView = this.entitiesDataView(
            ancestorsEntities,
            nodeEntities,
            entities
        );
        const entitiesDataViewIndex = this.entitiesDataViewIndex(
            ancestorsEntities,
            nodeEntities,
            entities
        );
        const entitiesSuggestions = this.entitiesSuggestions(
            entities,
            ancestorsEntities,
            nodeEntities,
            descendantsEntities
        );
        this.state = {
            entities: {
                dataView: entitiesDataView,
                dataViewIndex: entitiesDataViewIndex,
                filter: null,
                queryResult: {
                    dataView: entitiesDataView,
                    orResults: [],
                    excludedItemIds: null,
                },
            },
            entitiesSuggestions: {
                list: entitiesSuggestions,
                filter: null,
                filteredList: entitiesSuggestions,
            },
            lastAddedEntityId: undefined,
            dialog: undefined,
            entityAutoCompleteKey: 1,
        };
    }

    componentDidUpdate() {
        const {
            ancestorsEntities,
            nodeEntities,
            descendantsEntities,
            entities,
        } = this.props;
        const entitiesDataView = this.entitiesDataView(
            ancestorsEntities,
            nodeEntities,
            entities
        );
        const entitiesDataViewIndex = this.entitiesDataViewIndex(
            ancestorsEntities,
            nodeEntities,
            entities
        );
        const entitiesSuggestions = this.entitiesSuggestions(
            entities,
            ancestorsEntities,
            nodeEntities,
            descendantsEntities
        );
        if (this.state.entities.dataView !== entitiesDataView) {
            const { filter } = this.state.entities;
            const queryResult = entitiesDataViewIndex.find(filter);
            this.setState({
                entities: {
                    dataView: entitiesDataView,
                    dataViewIndex: entitiesDataViewIndex,
                    filter,
                    queryResult,
                },
            });
        }
        if (this.state.entitiesSuggestions.list !== entitiesSuggestions) {
            const { filter } = this.state.entitiesSuggestions;
            this.setState({
                entitiesSuggestions: {
                    list: entitiesSuggestions,
                    filter,
                    filteredList: this.filterEntitiesSuggestions(
                        entitiesSuggestions,
                        filter
                    ),
                },
            });
        }
    }

    render = () => {
        const { canDeleteEntityLink, onChange } = this.props;
        const { lastAddedEntityId } = this.state;

        const dataSource = DV.toArray(
            this.state.entities.queryResult.dataView.dataSource.iter()
        );

        return (
            <div className={theme.tableContainer}>
                {this.renderDialog()}
                <AssignEntitiesTable
                    entities={dataSource}
                    canDeleteEntityLink={canDeleteEntityLink}
                    lastAddedEntityId={lastAddedEntityId}
                    onConfirmUnassign={this.confirmUnassignEntity}
                />
                {onChange !== undefined ? (
                    <div className={theme.entitiesSuggestionsRow}>
                        <AutoComplete
                            key={this.state.entityAutoCompleteKey.toString()}
                            className={theme.entitiesSuggestions}
                            getPopupContainer={getParentElement}
                            placeholder={t(
                                "RiskDirectory_NodeUpdate_Entities_AssignEntity"
                            )}
                            dataSource={this.state.entitiesSuggestions.filteredList.map(
                                (entity) => ({
                                    value: entity.id.toString(),
                                    text: entity.name,
                                })
                            )}
                            onSearch={(filter) => {
                                const { list } = this.state.entitiesSuggestions;
                                const filterLc = filter.toLowerCase();
                                const filteredList = list.filter(
                                    (entity) =>
                                        entity.name
                                            .toLowerCase()
                                            .indexOf(filterLc) >= 0
                                );
                                this.setState({
                                    entitiesSuggestions: {
                                        list,
                                        filter,
                                        filteredList,
                                    },
                                });
                            }}
                            onSelect={this.assignEntity}
                        />
                    </div>
                ) : null}
            </div>
        );
    };

    renderDialog = () => {
        const { dialog } = this.state;
        if (!dialog) {
            return null;
        }
        return (
            <InformationModal
                open
                onClose={() => this.setState({ dialog: undefined })}
                type="warning"
                title={t(
                    "RiskDirectory_NodeUpdate_Entities_UnassignDialogTitle"
                )}
                footer={
                    <>
                        <Button
                            onClick={() => this.setState({ dialog: undefined })}
                        >
                            {t("Cancel")}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => {
                                this.unassignEntity(dialog.entityId);
                                this.setState({ dialog: undefined });
                            }}
                        >
                            {t("Unassign")}
                        </Button>
                    </>
                }
            >
                {renderMarkdown(
                    t("RiskDirectory_NodeUpdate_Entities_UnassignDialogBody")
                )}
            </InformationModal>
        );
    };

    buildEntitiesDataView = (
        ancestorsEntities: Record<number, string>,
        nodeEntities: number[],
        entities: Record<number, EntityView>
    ): DV.DataView<TableEntity> => {
        const data = Object.entries(ancestorsEntities)
            .map(([entityId, path]) => {
                const te: TableEntity = {
                    entity: entities[Number(entityId)],
                    scope: { type: "ANCESTOR", path },
                };
                return te;
            })
            .concat(
                nodeEntities.map((entityId) => {
                    const te: TableEntity = {
                        entity: entities[entityId],
                        scope: { type: "NODE" },
                    };
                    return te;
                })
            );
        const stringCompare = Cmp.string();
        data.sort(Cmp.map((te) => te.entity.name, stringCompare));
        const dataSource = new DV.ArrayDataSource(data, (entity) =>
            entity.entity.id.toString()
        );
        return {
            dataSource,
            extractorGroups: [
                {
                    id: "standard",
                    label: "Standard",
                    extractors: [
                        {
                            id: "name",
                            label: "",
                            getValue: (row) => row.entity.name,
                            compare: Cmp.map(
                                (row) => row.entity.name,
                                stringCompare
                            ),
                            asString: (row) => row.entity.name,
                        },
                    ],
                },
            ],
            sorters: [],
            defaultCompareItems: Cmp.map(
                (item) => item.entity.name,
                stringCompare
            ),
        };
    };
    buildEntitiesDataViewIndex = (
        dataView: DV.DataView<TableEntity>
    ): DataViewIndex<TableEntity> => {
        return new DataViewIndex(dataView, ["name"], [], []);
    };
    buildEntitiesSuggestions = (
        entities: Record<number, EntityView>,
        ancestorsEntities: Record<number, string>,
        nodeEntities: number[],
        descendantsEntities?: Record<number, string[]>
    ): Array<EntityView> => {
        const excluded: Record<number, boolean> = {};
        Object.keys(ancestorsEntities).forEach(
            (entityId) => (excluded[Number(entityId)] = true)
        );
        nodeEntities.forEach((entityId) => (excluded[entityId] = true));
        Object.keys(descendantsEntities ?? {}).forEach(
            (entityId) => (excluded[Number(entityId)] = true)
        );
        const suggestions = Object.values(entities).filter(
            (entity) => !excluded[entity.id]
        );
        suggestions.sort(Cmp.map((entity) => entity.name, Cmp.string()));
        return suggestions;
    };

    private updateFilter = (
        extractorId: string,
        fieldFilter: SimpleFieldFilter,
        excluded: boolean
    ) => {
        const oldFilter = this.state.entities.filter;
        const filter = addFieldFilter(
            extractorId,
            fieldFilter,
            excluded,
            oldFilter
        );
        this.setState({
            entities: {
                ...this.state.entities,
                filter,
                queryResult: this.state.entities.dataViewIndex.find(filter),
            },
        });
    };
    private removeFilter = (
        extractorId: string,
        fieldFilter: SimpleFieldFilter,
        excluded: boolean
    ) => {
        const oldFilter = this.state.entities.filter;
        if (oldFilter) {
            const filter = removeFieldFilter(
                extractorId,
                fieldFilter,
                excluded,
                oldFilter
            );
            this.setState({
                entities: {
                    ...this.state.entities,
                    filter,
                    queryResult: this.state.entities.dataViewIndex.find(filter),
                },
            });
        }
    };
    private removeAllFilters = () => {
        const filter = null;
        this.setState({
            entities: {
                ...this.state.entities,
                filter,
                queryResult: this.state.entities.dataViewIndex.find(filter),
            },
        });
    };
    private confirmUnassignEntity = (entityId: number) => {
        if (this.props.confirmUnassign === false) {
            return this.unassignEntity(entityId);
        }
        const { riskDirectoryView, node } = this.props.confirmUnassign;
        getRisksRiskDirectoryIdNodes(riskDirectoryView.content.resource.id, {
            only_with_linked_data: true,
        }).then((response) => {
            const dataSource: DV.DataSource<
                DV.NodeDescriptor<T.DirectoryNode>
            > = new DV.MultiTreeDataSource<T.DirectoryNode>(
                [response.data.content.resource.rootNode],
                getDirectoryChildren,
                getId,
                (node) => node.content.name
            );
            const dataNode = DV.find(
                dataSource.iter(),
                (desc) => desc.value.id === node.id
            );
            let hasEntityData = false;
            if (dataNode) {
                const subDataSource =
                    new DV.MultiTreeDataSource<T.DirectoryNode>(
                        [dataNode.value],
                        getDirectoryChildren,
                        getId,
                        (node) => node.content.name
                    );
                const foundNodeWithData = DV.find(
                    subDataSource.iter(),
                    (desc) => {
                        switch (desc.value.type) {
                            case "RiskDirectory":
                            case "Process": {
                                return false;
                            }
                            case "Risk": {
                                const riskScope = desc.value.content.scope;
                                switch (riskScope.type) {
                                    case "GlobalWithContent": {
                                        const content =
                                            riskScope.entityIdContent[entityId];
                                        if (content !== undefined) {
                                            return (
                                                !isEmpty(
                                                    content.metadataValues
                                                ) ||
                                                !isEmpty(
                                                    content.mitigatingControls
                                                )
                                            );
                                        } else {
                                            return false;
                                        }
                                    }
                                    case "LocalWithContent": {
                                        if (riskScope.entityId === entityId) {
                                            const { content } = riskScope;
                                            return (
                                                !isEmpty(
                                                    content.metadataValues
                                                ) ||
                                                !isEmpty(
                                                    content.mitigatingControls
                                                )
                                            );
                                        } else {
                                            return false;
                                        }
                                    }
                                    default: {
                                        return assertNever(riskScope);
                                    }
                                }
                            }
                            default: {
                                return assertNever(desc.value);
                            }
                        }
                    }
                );
                if (foundNodeWithData !== undefined) {
                    hasEntityData = true;
                }
            }
            if (hasEntityData) {
                this.setState({
                    dialog: { type: "CONFIRM_UNASSIGN", entityId },
                });
            } else {
                this.unassignEntity(entityId);
            }
        });
    };
    private assignEntity = (value: SelectValue) => {
        if (this.props.onChange === undefined) {
            return;
        }
        if (typeof value === "string") {
            const entityId = Number(value);
            if (this.props.onAction !== undefined) {
                this.props.onAction(addEntityId, entityId);
            }
            this.props
                .onChange(addEntityId(this.props.nodeEntities, entityId))
                .then(() =>
                    this.setState({
                        lastAddedEntityId: entityId,
                        entityAutoCompleteKey:
                            this.state.entityAutoCompleteKey + 1,
                    })
                );
        } else {
            throw new Error("Not implemented");
        }
    };
    private unassignEntity = (entityId: number) => {
        if (this.props.onChange === undefined) {
            return;
        }
        if (this.props.onAction !== undefined) {
            this.props.onAction(removeEntityId, entityId);
        }
        this.props
            .onChange(removeEntityId(this.props.nodeEntities, entityId))
            .then(() => this.resetLastAddedEntity());
    };
    private resetLastAddedEntity = () => {
        this.setState({
            lastAddedEntityId: undefined,
        });
    };
    private filterEntitiesSuggestions = (
        list: Array<EntityView>,
        filter: string | null
    ): Array<EntityView> => {
        if (filter) {
            const filterLc = filter.toLowerCase();
            return list.filter(
                (entity) => entity.name.toLowerCase().indexOf(filterLc) >= 0
            );
        } else {
            return list;
        }
    };
}

function addEntityId(
    entityIds: Array<number>,
    entityIdToAdd: number
): Array<number> {
    if (!(entityIds.indexOf(entityIdToAdd) >= 0)) {
        const newEntityIds = entityIds.slice();
        newEntityIds.push(entityIdToAdd);
        return newEntityIds;
    } else {
        // No change, return old value.
        return entityIds;
    }
}

function removeEntityId(
    entityIds: Array<number>,
    entityIdToRemove: number
): Array<number> {
    const newEntityIds = entityIds.filter(
        (entityId) => entityId !== entityIdToRemove
    );
    if (newEntityIds.length !== entityIds.length) {
        return newEntityIds;
    } else {
        // No change, return old value.
        return entityIds;
    }
}

type Action = (entityIds: Array<number>, entityId: number) => Array<number>;

interface AssignEntitiesComponentProps {
    ancestorsEntities: Record<number, string>;
    nodeEntities: number[];
    descendantsEntities?: Record<number, string[]>;
    entities: Record<number, EntityView>;
    canDeleteEntityLink: boolean;
    confirmUnassign:
        | {
              riskDirectoryView: R.RiskDirectoryView;
              node: R.RiskDirectoryNode | R.StandardNode;
          }
        | false;

    // Edit props
    onChange?: (entityIds: number[]) => Promise<void>;
    onAction?: (action: Action, entityId: number) => void;
}

interface AssignEntitiesComponentState {
    entities: EntitiesFilterState;
    entitiesSuggestions: EntitiesSuggestionsFilterState;
    lastAddedEntityId: number | undefined;
    dialog: { type: "CONFIRM_UNASSIGN"; entityId: number } | undefined;
    // Only used to reset the autocomplete value when an entity is selected
    entityAutoCompleteKey: number;
}

interface EntitiesFilterState {
    dataView: DV.DataView<TableEntity>;
    dataViewIndex: DataViewIndex<TableEntity>;
    filter: SimpleAndFilter | null;
    queryResult: QueryResult<TableEntity>;
}

interface EntitiesSuggestionsFilterState {
    list: Array<EntityView>;
    filter: string | null;
    filteredList: Array<EntityView>;
}

class CreateAssignEntities<ID extends string, ROOT> extends React.PureComponent<
    CreateAssignEntitiesProps<ID, ROOT>
> {
    render = () => {
        const { form, id, validators, initialValue, ...rest } = this.props;
        const { getFieldDecorator, setFieldsValue } = form;

        // purposely exclude nodeEntities & onClick props, managed by getFieldDecorator
        const componentProps: Omit<
            AssignEntitiesComponentProps,
            "nodeEntities" | "onChange"
        > = rest;

        return getFieldDecorator(id, {
            rules: validators,
            valuePropName: "nodeEntities",
            trigger: "", // All triggers are handled by direct calls to setFieldsValue because onChange need to return a promise.
            validateFirst: true,
            preserve: true,
            initialValue: castValue(id, form, initialValue),
        })(
            <AssignEntitiesComponent
                {...(componentProps as any)}
                onChange={(entityIds) => {
                    return new Promise<void>((resolve) => {
                        setFieldsValue(
                            casterWithIdWrapper<number[]>()(
                                id,
                                form,
                                entityIds
                            ),
                            resolve
                        );
                    });
                }}
            />
        );
    };
}

interface CreateAssignEntitiesProps<ID extends string, ROOT>
    extends
        FormComponentProps<ID, number[], ROOT>,
        Omit<AssignEntitiesComponentProps, "nodeEntities" | "onChange"> {
    id: ID;
    initialValue: number[] | undefined;
    validators?: ValidationRule<number[]>[];
}

const EditAssignEntitiesForm = EditFormCreate<number[]>()(CreateAssignEntities);

const EditAssignEntities = EditInput<number[]>(EditAssignEntitiesForm);

interface EditAssignEntitiesProps
    extends
        EditProps<number[] | undefined>,
        Omit<AssignEntitiesComponentProps, "nodeEntities" | "onChange"> {}

class ReadOnlyAssignEntities extends React.PureComponent<ReadOnlyAssignEntitiesProps> {
    render = () => {
        const { ancestorsEntities, nodeEntities, entities } = this.props;
        return (
            <AssignEntitiesComponent
                ancestorsEntities={ancestorsEntities}
                nodeEntities={nodeEntities}
                entities={entities}
                canDeleteEntityLink={false}
                confirmUnassign={false}
            />
        );
    };
}

type ReadOnlyAssignEntitiesProps = Omit<
    AssignEntitiesComponentProps,
    "canDeleteEntityLink" | "confirmUnassign" | "onChange"
>;

class AssignEntities<ID extends string, ROOT> extends React.PureComponent<
    AssignEntitiesProps<ID, ROOT>
> {
    render = () => {
        switch (this.props.type) {
            case "READ_ONLY": {
                const { type: _type, ...rest } = this.props;
                return <ReadOnlyAssignEntities {...rest} />;
            }
            case "EDIT": {
                const { type: _type, ...rest } = this.props;
                return <EditAssignEntities {...rest} />;
            }
            case "CREATE": {
                const { type: _type, ...rest } = this.props;
                return <CreateAssignEntities {...rest} />;
            }
            default:
                return assertNever(this.props);
        }
    };
}

type AssignEntitiesProps<ID extends string, ROOT> =
    | ({ type: "READ_ONLY" } & ReadOnlyAssignEntitiesProps)
    | ({ type: "EDIT" } & EditAssignEntitiesProps)
    | ({ type: "CREATE" } & CreateAssignEntitiesProps<ID, ROOT>);

export { AssignEntities, type AssignEntitiesProps, type Action };
